// React
import { useEffect, useState } from 'react';

// Store
import useProfileStore from '../../store/profileStore.js';
import useAuthStore from '../../store/authStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Component
import { AppBar, Avatar, Badge, Button, IconButton, Toolbar, Typography, Box, Chip, Tabs, Tab, Tooltip, useMediaQuery } from '@mui/material';
import { LogoutOutlined, RefreshOutlined, PersonOutlined, DarkModeOutlined, LightModeOutlined, ScheduleOutlined, NotificationsOutlined } from '@mui/icons-material';

// Style
import { grey, green, red, blue, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Other
import { formatProfileType } from '../../utils/index.js';
import useSiteStore from '../../store/siteStore.js';
import FeedbackNotificationsDialog from '../dialog/FeedbackNotificationsDialog.js';
import { dismissFeedbackNotificationsForCoach, dismissOneFeedbackNotificationForCoach, getFeedbackNotificationsForCoach } from '../../service/httpService/feedback/index.js';

function DashboardAppBar() {
	const { profile, profilePicture, profileType, setOpenEditProfile } = useProfileStore();
	const { mobile, selectedTab, setSelectedTab } = useSiteStore();
	const { doLogout, sessionTimeLeft } = useAuthStore();
	const { isDarkMode, toggleDarkMode } = useAppTheme();
	const isTablet = useMediaQuery('(min-width:601px) and (max-width:1000px)');

	// Use mobile-like two-row layout for both mobile and tablet
	const compactLayout = mobile || isTablet;

	const isWarning = sessionTimeLeft <= 60;
	const [openFeedbackNotifications, setOpenFeedbackNotifications] = useState(false);
	const [feedbackNotifications, setFeedbackNotifications] = useState([]);
	const [loadingFeedbackNotifications, setLoadingFeedbackNotifications] = useState(false);

	const canSeeFeedbackNotifications = profileType === 'coach' || profileType === 'nationalCoach';

	const loadFeedbackNotifications = async () => {
		if (!canSeeFeedbackNotifications || !profile?.email) return;
		setLoadingFeedbackNotifications(true);
		const res = await getFeedbackNotificationsForCoach(profile.email, 50).catch(() => null);
		setFeedbackNotifications(res?.data || []);
		setLoadingFeedbackNotifications(false);
	};

	const dismissAllFeedbackNotifications = async () => {
		if (!canSeeFeedbackNotifications || !profile?.email) return;
		setLoadingFeedbackNotifications(true);
		await dismissFeedbackNotificationsForCoach(profile.email).catch(() => null);
		setFeedbackNotifications([]);
		setLoadingFeedbackNotifications(false);
	};

	const dismissOneFeedbackNotification = async (notification) => {
		if (!canSeeFeedbackNotifications || !profile?.email) return;
		const feedbackId = notification?.feedbackId;
		if (!feedbackId) return;
		setLoadingFeedbackNotifications(true);
		await dismissOneFeedbackNotificationForCoach(profile.email, feedbackId).catch(() => null);
		setFeedbackNotifications(prev => (prev || []).filter(n => n?.feedbackId !== feedbackId));
		setLoadingFeedbackNotifications(false);
	};

	const openWeeklyScheduleFromNotification = (notification) => {
		const athleteEmail = notification?.athlete?.email;
		if (!athleteEmail) return;
		const date = notification?.trainingSession?.date || notification?.feedbackDate;
		if (!date) return;
		const trainingSessionId = notification?.trainingSession?.id;

		setSelectedTab(0);
		setOpenFeedbackNotifications(false);

		try {
			window.dispatchEvent(
				new CustomEvent('simon:openWeeklyScheduleFromNotification', {
					detail: {
						athleteEmail,
						athlete: notification?.athlete,
						date,
						trainingSessionId,
						isMobile: !!mobile,
					},
				})
			);
		} catch {
			// ignore
		}
	};

	useEffect(() => {
		loadFeedbackNotifications();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profileType, profile?.email]);

	return (
		<AppBar
			position='static'
			elevation={0}
			sx={{
				background: isDarkMode
					? 'linear-gradient(135deg, #1a1a1a 0%, #242424 100%)'
					: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.98) 100%)',
				backdropFilter: 'blur(20px)',
				borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[200]}`,
				boxShadow: isDarkMode ? '0 2px 12px rgba(0, 0, 0, 0.3)' : '0 2px 12px rgba(0, 0, 0, 0.08)',
				mt: 0,
				pt: 0,
				transition: 'all 0.3s ease',
			}}
		>
			<Toolbar
				sx={{
					py: 1.5,
					minHeight: '72px',
					position: 'relative',
				}}
			>
				{/* Avatar */}
				<IconButton
					sx={{
						mr: 1,
						p: 0.5,
						transition: 'transform 0.2s ease',
						'&:hover': {
							transform: 'scale(1.05)',
						}
					}}
					size='large'
					edge='start'
					onClick={() => setOpenEditProfile(true)}
				>
					{profile && !profilePicture && (
						<Avatar
							sx={{
								width: 48,
								height: 48,
								bgcolor: isDarkMode ? orange[600] : blue[600],
								color: isDarkMode ? '#1a1a1a' : 'white',
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
								fontWeight: 600,
								fontSize: '1.1rem',
							}}
						>

						</Avatar>
					)}

					{profile && profilePicture && (
						<Avatar
							src={URL.createObjectURL(profilePicture)}
							sx={{
								width: 48,
								height: 48,
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
							}}
						/>
					)}
				</IconButton>

				{/* User Info */}
				{profile && profileType && (
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 0.3,
						mr: compactLayout ? 'auto' : 2
					}}>
						<Typography
							variant='subtitle1'
							sx={{
								fontWeight: 600,
								color: isDarkMode ? darkTheme.textPrimary : grey[800],
								lineHeight: 1.2
							}}
						>
							{profile.firstName} {profile.lastName}
						</Typography>

						<Chip
							icon={<PersonOutlined sx={{ fontSize: 14 }} />}
							label={formatProfileType(profileType)}
							size="small"
							sx={{
								height: 22,
								fontSize: '0.75rem',
								fontWeight: 500,
								backgroundColor: isDarkMode ? darkTheme.cardBg : blue[50],
								color: isDarkMode ? orange[500] : blue[700],
								border: isDarkMode ? `1px solid ${orange[500]}` : `1px solid ${blue[200]}`,
								alignSelf: 'flex-start',
								'& .MuiChip-icon': {
									color: isDarkMode ? orange[500] : blue[600],
								}
							}}
						/>
					</Box>
				)}

				{/* Navigation Tabs - National Coach */}
				{!compactLayout && profileType && profileType === 'nationalCoach' && (
					<Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pt: 0.5 }}>
						<Tabs
							value={selectedTab}
							onChange={(event, newValue) => setSelectedTab(newValue)}
							sx={{
								overflow: 'visible',
								'& .MuiTabs-indicator': {
									display: 'none',
								},
								'& .MuiTabs-flexContainer': {
									gap: 1,
								},
								'& .MuiTabs-scroller': {
									overflow: 'visible !important',
								}
							}}
						>
							<Tab
								label="Athleten"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],

									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Trainer"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Eltern"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
						</Tabs>
					</Box>
				)}

				{/* Navigation Tabs - Coach */}
				{!compactLayout && profileType && profileType === 'coach' && (
					<Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pt: 0.5 }}>
						<Tabs
							value={selectedTab}
							onChange={(event, newValue) => setSelectedTab(newValue)}
							sx={{
								overflow: 'visible',
								'& .MuiTabs-indicator': {
									display: 'none',
								},
								'& .MuiTabs-flexContainer': {
									gap: 1,
								},
								'& .MuiTabs-scroller': {
									overflow: 'visible !important',
								}
							}}
						>
							<Tab
								label="Athleten"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Eltern"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
						</Tabs>
					</Box>
				)}

				{/* Navigation Tabs - Athlete */}
				{!compactLayout && profileType && profileType === 'athlete' && (
					<Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pt: 0.5 }}>
						<Tabs
							value={selectedTab === 3 ? 0 : selectedTab}
							onChange={(event, newValue) => setSelectedTab(newValue)}
							sx={{
								overflow: 'visible',
								'& .MuiTabs-indicator': {
									display: 'none',
								},
								'& .MuiTabs-flexContainer': {
									gap: 1,
								},
								'& .MuiTabs-scroller': {
									overflow: 'visible !important',
								}
							}}
						>
							<Tab
								label="Wochenplan"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Trainerteam"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Eltern"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
						</Tabs>
					</Box>
				)}

				{/* Navigation Tabs - Parent */}
				{!compactLayout && profileType && profileType === 'parent' && (
					<Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pt: 0.5 }}>
						<Tabs
							value={selectedTab}
							onChange={(event, newValue) => setSelectedTab(newValue)}
							sx={{
								overflow: 'visible',
								'& .MuiTabs-indicator': {
									display: 'none',
								},
								'& .MuiTabs-flexContainer': {
									gap: 1,
								},
								'& .MuiTabs-scroller': {
									overflow: 'visible !important',
								}
							}}
						>
							<Tab
								label="Wochenplan"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
							<Tab
								label="Trainerteam"
								sx={{
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '0.9rem',
									minHeight: 40,
									color: isDarkMode ? darkTheme.textSecondary : grey[700],
									px: 3,
									py: 1,
									minWidth: 100,
									borderRadius: '20px',
									border: isDarkMode ? `1.5px solid ${darkTheme.cardBorder}` : `1.5px solid ${grey[300]}`,
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									transition: 'all 0.3s ease',
									'&:hover': {
										color: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
									},
									'&.Mui-selected': {
										color: isDarkMode ? '#1a1a1a' : 'white',
										backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
										borderColor: isDarkMode ? darkTheme.accent : blue[600],
										fontWeight: 600,
										boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
										'&:hover': {
											backgroundColor: isDarkMode ? orange[600] : blue[700],
											borderColor: isDarkMode ? orange[600] : blue[700],
											color: isDarkMode ? '#1a1a1a' : 'white',
										}
									}
								}}
							/>
						</Tabs>
					</Box>
				)}

				{/* Spacer to push action buttons to the right */}
				<Box sx={{ flexGrow: 1 }} />

				{/* Action Buttons */}
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					{/* Dark Mode Toggle - Available to all users */}
					<Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'}>
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								backgroundColor: isDarkMode ? grey[800] : 'white',
								border: `1px solid ${isDarkMode ? grey[600] : grey[300]}`,
								borderRadius: 2,
								transition: 'all 0.2s ease',
								'&:hover': {
									backgroundColor: isDarkMode ? grey[700] : orange[50],
									borderColor: orange[400],
								}
							}}
						>
							{isDarkMode
								? <LightModeOutlined sx={{ color: orange[400], fontSize: 20 }} />
								: <DarkModeOutlined sx={{ color: grey[700], fontSize: 20 }} />
							}
						</IconButton>
					</Tooltip>

					{/* Feedback Notifications (Coach) */}
					{canSeeFeedbackNotifications && (
						<Badge
							variant="dot"
							overlap="circular"
							invisible={!(feedbackNotifications && feedbackNotifications.length > 0)}
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							sx={{
								'& .MuiBadge-badge': {
									backgroundColor: isDarkMode ? orange[400] : blue[600],
									color: isDarkMode ? orange[400] : blue[600],
									top: 6,
									right: 6,
								}
							}}
						>
							<IconButton
								onClick={() => {
									setOpenFeedbackNotifications(true);
									loadFeedbackNotifications();
								}}
								sx={{
									backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
									border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
									borderRadius: 2,
									transition: 'all 0.2s ease',
									'&:hover': {
										backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
										borderColor: isDarkMode ? darkTheme.accent : blue[300],
									}
								}}
							>
								<NotificationsOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[700], fontSize: 20 }} />
							</IconButton>
						</Badge>
					)}
					{/* Refresh Button */}
					<Tooltip title="Seite aktualisieren">
						<IconButton
							onClick={() => window.location.reload()}
							sx={{
								backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
								border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
								borderRadius: 2,
								transition: 'all 0.2s ease',
								'&:hover': {
									backgroundColor: isDarkMode ? darkTheme.accent : green[50],
									borderColor: isDarkMode ? darkTheme.accent : green[300],
									transform: 'rotate(180deg)',
									'& .MuiSvgIcon-root': {
										color: isDarkMode ? '#1a1a1a' : green[700],
									}
								}
							}}
						>
							<RefreshOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[700], fontSize: 20 }} />
						</IconButton>
					</Tooltip>

					{/* Session Time Button */}
					<Tooltip title={`Session endet in ${Math.floor(sessionTimeLeft / 60)}:${(sessionTimeLeft % 60).toString().padStart(2, '0')}`}>
						<IconButton
							sx={{
								backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
								border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
								borderRadius: 2,
								transition: 'all 0.2s ease',
								minWidth: '48px',
								'&:hover': {
									backgroundColor: isDarkMode ? darkTheme.accent : blue[50],
									borderColor: isDarkMode ? darkTheme.accent : blue[300],
									'& .MuiSvgIcon-root': {
										color: isDarkMode ? '#1a1a1a' : blue[700],
									},
									'& .MuiTypography-root': {
										color: isDarkMode ? '#1a1a1a' : blue[700],
									}
								}
							}}
						>
							<ScheduleOutlined sx={{ color: isWarning ? red[500] : (isDarkMode ? darkTheme.textSecondary : grey[700]), fontSize: 20 }} />
							<Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600, color: isWarning ? red[500] : (isDarkMode ? darkTheme.textSecondary : grey[700]) }}>
								{Math.ceil(sessionTimeLeft / 60)}
							</Typography>
						</IconButton>
					</Tooltip>

					{/* Logout Button */}
					{!compactLayout && (
						<Button
							variant="outlined"
							onClick={() => doLogout()}
							startIcon={<LogoutOutlined />}
							sx={{
								color: isDarkMode ? darkTheme.textSecondary : grey[700],
								borderColor: isDarkMode ? darkTheme.cardBorder : grey[300],
								backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
								borderRadius: 2,
								textTransform: 'none',
								fontWeight: 500,
								px: 2,
								'&:hover': {
									backgroundColor: isDarkMode ? darkTheme.cardHover : red[50],
									borderColor: isDarkMode ? darkTheme.accent : red[300],
									color: isDarkMode ? darkTheme.accent : red[700],
								}
							}}
						>
							Abmelden
						</Button>
					)}

					{compactLayout && (
						<IconButton
							onClick={() => doLogout()}
							sx={{
								backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
								border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
								borderRadius: 2,
								transition: 'all 0.2s ease',
								'&:hover': {
									backgroundColor: isDarkMode ? darkTheme.cardHover : red[50],
									borderColor: isDarkMode ? darkTheme.accent : red[300],
								}
							}}
						>
							<LogoutOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[700], fontSize: 20 }} />
						</IconButton>
					)}
				</Box>

				{canSeeFeedbackNotifications && (
					<FeedbackNotificationsDialog
						open={openFeedbackNotifications}
						onClose={() => setOpenFeedbackNotifications(false)}
						notifications={feedbackNotifications}
						loading={loadingFeedbackNotifications}
						onDismissAll={dismissAllFeedbackNotifications}
						onDismissOne={dismissOneFeedbackNotification}
						onOpenNotification={openWeeklyScheduleFromNotification}
					/>
				)}
			</Toolbar>
		</AppBar>
	)
}

export default DashboardAppBar;